<template>
  <vx-card class="mt-base" title="5. The Scalp & Hair">
    <p class="mb-5">
      If the scalp isn’t in good condition, the hair won’t be either. It’s essential to determine
      the condition of the scalp before choosing suitable products and treatments.
    </p>

    <h4 class="mb-5">Take the Scalp Test: How Oily or Dry Is Your Hair?</h4>

    <p class="mb-5">
      There are a few simple ways to test whether your hair is oily, medium, or dry.
    </p>
    <p class="mb-5">
      First, wash your hair, but don’t put any products in your hair afterwards. Leave your hair
      overnight.
    </p>
    <p class="mb-5">
      The next day take a piece of tissue paper and press it against your scalp and the roots of
      your hair. Then, examine the tissue paper closely.
    </p>

    <ul class="default-list">
      <li>If the tissue paper has an oily residue, then you have oily hair.</li>
      <li>If the tissue paper has a trace of oil but not too much, then you have medium hair.</li>
      <li>If the tissue paper has no oily residue, then you have dry hair.</li>
    </ul>

    <p class="mb-5"><strong>Flaky Scalp</strong></p>
    <p class="mb-5">A flaky scalp can be an indicator of both oily and dry hair.</p>
    <p class="mb-5">
      Frequently, when someone realises they have a flaky and itchy scalp, they’ll assume they have
      dandruff. And that’s certainly a possibility, but they could also be suffering from a dry
      scalp.
    </p>
    <p class="mb-5">Dandruff and a dry scalp have two main symptoms in common:</p>

    <ul class="default-list">
      <li>Falling flakes of skin</li>
      <li>An itchy scalp</li>
    </ul>

    <p class="mb-5">However, these two conditions are very different.</p>
    <p class="mb-5">
      An excessively oily scalp causes dandruff. This excess oil causes skin cells to build up and
      then flake off.
    </p>
    <p class="mb-5">
      The skin gets irritated with a dry scalp due to lack of moisture, and then the flakes fall
      off.
    </p>
    <p class="mb-5">
      Take a look at the table below to learn to tell the difference between dandruff and dry scalp.
    </p>

    <centered-image-wrapper
      :image="require('@/assets/images/experiences/hairdressing/activity_51.png')"
      classes="xl:w-1/2 lg:w-2/3 w-full"
      alt="Table comparing dandruff and dry scalp. Dandruff: oily, large flakes that are yellow or white; itchy scalp; oily, red, scaly skin. Dry scalp: smaller, dry flakes; itchy scalp; dry skin on other parts of your body."
    />

    <p class="mb-5">Now, you know how to tell if you have an oily, medium, or dry scalp.</p>
    <div v-for="(question, index) in questions" :key="index">
      <h5 class="mb-5 questionlabel">{{ question.question }}</h5>
      <div class="vx-row my-5">
        <div class="vx-col w-full mb-2" v-if="isEditable">
          <select
            class="default-select"
            v-model="answers['question' + index]"
            :aria-label="question.question"
          >
            <option v-for="answer in question.answers" :key="answer.val" :value="answer.val">
              {{ answer.answer }}
            </option>
          </select>
        </div>
        <div class="my-2 mx-5" v-else>
          <p
            class="my-2"
            v-for="answer in question.answers"
            :key="answer.val"
            v-if="answers['question' + index] != 0"
          >
            <span v-if="answers['question' + index] == answer.val">{{ answer.answer }}</span>
          </p>
          <p class="my-2" v-else>N/A</p>
        </div>
      </div>
    </div>

    <h4 class="mb-5">The Scalp: What Does This Mean for Hair Treatment?</h4>

    <p class="mb-5"><strong>For oily hair and scalp:</strong></p>

    <ul class="default-list">
      <li>Wash less often.</li>
      <li>Use lukewarm water with shampoo.</li>
      <li>No rubbing or massaging with shampoo.</li>
      <li>Use oily hair and scalp products.</li>
      <li>Avoid using too many styling products on the roots.</li>
    </ul>
    <p class="mb-5"><strong>For dry hair scalp:</strong></p>
    <ul class="default-list">
      <li>Keep ends moisturised to prevent split ends.</li>
      <li>Use hydrating products.</li>
      <li>Avoid heat styling tools.</li>
      <li>Use nourishing conditioner.</li>
      <li>Avoid excessively washing hair.</li>
      <li>Don’t overuse bleach and hair dyes.</li>
      <li>Use cap when swimming in chlorinated pools.</li>
    </ul>

    <p class="mb-5"><strong>For dandruff:</strong></p>
    <ul class="default-list">
      <li>Limit the use of hair care products.</li>
      <li>Wash frequently to reduce oiliness.</li>
      <li>Shampoo often gently massaging to release flakes.</li>
      <li>Use medicated shampoo.</li>
      <li>Get sunlight, where possible.</li>
    </ul>
    <save-buttons
      :is-editable="isEditable"
      @save="save"
      :student-back="
        '/student/work-experience/' + this.$route.params.id + '/activities/identifying-hair-types'
      "
      :teacher-back="
        '/teacher/vje/experience/view/' +
        this.$route.params.id +
        '/activities/identifying-hair-types'
      "
    />
  </vx-card>
</template>

<script>
import computed from '@/views/WorkExperiences/components/activity_js/computed';
import inject from '@/views/WorkExperiences/components/activity_js/inject';

export default {
  data() {
    return {
      questions: {
        1: {
          question: 'My scalp is:',
          answers: [
            { val: 0, answer: 'Select your scalp type' },
            { val: 1, answer: 'Oily' },
            { val: 2, answer: 'Medium' },
            { val: 3, answer: 'Dry' },
          ],
        },
      },
      answers: {
        question1: 0,
      },
    };
  },
  methods: {
    save() {
      this.data = this.answers;
      this.saveActivity(this.$route.params.slug, this.data, -1);
    },
  },
  computed: computed,
  inject: inject,
  mounted() {
    if (this.data.question1) {
      this.answers = this.data;
    }
  },
};
</script>
